import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import api from "api/api";

// mui components & icons
import { styled } from "@mui/material/styles";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ControlPointRoundedIcon from "@mui/icons-material/ControlPointRounded";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

// redux
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

// common components
import Loader from "../common/Loader";

// import helper
import ConsumerFilterOrder from "utils/ConsumerFilterOrder";

const Accordion = styled((props) => (
    <MuiAccordion
        disableGutters
        elevation={0}
        square
        {...props}
    />
))({
    border: "none",
    backgroundColor: "transparent",
    boxShadow: "none",

    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => <MuiAccordionSummary {...props} />)({
    backgroundColor: "transparent",
    flexDirection: "row-reverse",
    padding: 0,
    minHeight: "40px",

    "&.Mui-expanded": {
        minHeight: "40px",
    },

    "& .MuiAccordionSummary-content": {
        margin: 0,
    },

    "& .MuiAccordionSummary-content.Mui-expanded": {
        margin: 0,
    },

    "& .MuiAccordionSummary-expandIconWrapper": {
        marginRight: "8px",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 0px 16px",
    border: "none",
});

function UserProfileCreateUpdate({ isOpen = false, setIsOpen = () => {}, type = "create", filtersResponse = {}, consumerFilters = {}, setConsumerFilters = () => {}, userProfiles = [], getUserProfiles = () => {}, selectedUserProfiles = [] }) {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState("");
    const [updateExpaned, setUpdateExpanded] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [userProfileName, setUserProfileName] = useState("");
    const [profileNameError, setProfileNameError] = useState(false);
    const [matchedUserProfileTitle, setMatchedUserProfileTitle] = useState("");
    const [userProfileFiltersForUpdate, setUserProfileFiltersForUpdate] = useState({});

    const [expandedUserProfiles, setexpandedUserProfiles] = useState([]);

    const customOrderSub = [...ConsumerFilterOrder.customOrderSub];
    const alphabeticOrderFilters = [...ConsumerFilterOrder.alphabeticOrderFilters];

    // redux states
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    useEffect(() => {
        if (type === "update") {
            const updatedProfiles = {};
            userProfiles.map((profile) => {
                const selectedFilters = profile.user_selected_filters;
                const _consumerFilters = { ...consumerFilters };
                const cf = Object.fromEntries(
                    Object.entries(_consumerFilters).map(([key, options]) => [
                        key,
                        options.map((item) => ({
                            ...item,
                            checked: selectedFilters[key]?.includes(item.value) ?? false,
                        })),
                    ])
                );
                updatedProfiles[profile.title] = cf;
            });
            setUserProfileFiltersForUpdate(updatedProfiles);
        }
    }, [type, consumerFilters, userProfiles]);

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const handleUpdateChange = (title, panel) => (event, newExpanded) => {
        setUpdateExpanded((prev) => ({
            ...prev,
            [title]: newExpanded ? panel : false,
        }));
    };

    const handleReset = () => {
        const updatedData = Object.fromEntries(
            Object.entries(consumerFilters).map(([key, options]) => [
                key,
                options.map((option) => ({
                    ...option,
                    checked: false,
                })),
            ])
        );
        setConsumerFilters(updatedData);
        setUserProfileName("");
        setExpanded("");
        setMatchedUserProfileTitle("");
    };

    const handleUpdateReset = () => {
        const updatedProfiles = {};
        userProfiles.map((profile) => {
            const selectedFilters = profile.user_selected_filters;
            const _consumerFilters = { ...consumerFilters };
            const cf = Object.fromEntries(
                Object.entries(_consumerFilters).map(([key, options]) => [
                    key,
                    options.map((item) => ({
                        ...item,
                        checked: selectedFilters[key]?.includes(item.value) ?? false,
                    })),
                ])
            );
            updatedProfiles[profile.title] = cf;
        });
        setUserProfileFiltersForUpdate(updatedProfiles);
        setexpandedUserProfiles([]);
        setUpdateExpanded({});
        setMatchedUserProfileTitle("");
    };

    const handleClose = () => {
        type === "create" ? handleReset() : handleUpdateReset();
        setIsOpen(false);
    };

    const hanldeProfileNameChange = (name) => {
        setUserProfileName(name);
    };

    useEffect(() => {
        const isExists = userProfiles.some((profile) => profile.title?.trim().toLowerCase() === userProfileName.trim().toLowerCase());
        setProfileNameError(isExists ? isExists : false);
    }, [userProfiles, userProfileName]);

    const getCheckedFiltersData = (data) => {
        const selectedData = Object.fromEntries(
            Object.entries(data)
                .map(([key, options]) => [key, options.filter((option) => option.checked).map((option) => option.value)])
                .filter(([, values]) => values.length > 0)
        );
        return selectedData;
    };

    const isMatch = (data, comparisonData) => {
        const selectedData = getCheckedFiltersData(comparisonData);

        const keys1 = Object.keys(data).sort();
        const keys2 = Object.keys(selectedData).sort();

        if (JSON.stringify(keys1) !== JSON.stringify(keys2)) {
            return false;
        }

        return keys1.every((key) => {
            const values1 = [...data[key]].sort();
            const values2 = [...selectedData[key]].sort();

            return JSON.stringify(values1) === JSON.stringify(values2);
        });
    };

    const handleOnChange = (cf, label, backendVariable, title) => {
        const _filters = { ...cf };
        if (type === "create") {
            const updatedCheckedState = _filters[backendVariable].map((item) => {
                return item.value === label ? { ...item, checked: !item.checked } : { ...item };
            });
            _filters[backendVariable] = updatedCheckedState;
            const finalFilters = { ..._filters };
            setConsumerFilters(finalFilters);
            if (userProfiles.length > 0) {
                const macthedTitle = userProfiles.find((data) => isMatch(data.user_selected_filters, finalFilters))?.title ?? "";
                setMatchedUserProfileTitle(macthedTitle);
            } else {
                setMatchedUserProfileTitle("");
            }
        } else {
            const updatedCheckedState = _filters[backendVariable].map((item) => {
                return item.value === label ? { ...item, checked: !item.checked } : { ...item };
            });
            _filters[backendVariable] = updatedCheckedState;
            const finalFilters = { ..._filters };
            setUserProfileFiltersForUpdate((prev) => ({
                ...prev,
                [title]: finalFilters,
            }));
            if (Object.keys(userProfileFiltersForUpdate).length > 1) {
                const matchedEntry = Object.entries(userProfileFiltersForUpdate).find(([profileTitle, filters]) => profileTitle !== title && isMatch(getCheckedFiltersData(filters), finalFilters));
                const matchedTitle = matchedEntry?.[0] ?? "";
                // const macthedObject = userProfiles.find((data) => data.title !== title && isMatch(data.user_selected_filters, finalFilters)) ?? {};
                setMatchedUserProfileTitle(matchedTitle);
            } else {
                setMatchedUserProfileTitle("");
            }
        }
    };

    const sortSubFilters = (displayLabel, backendVariable) => {
        const selectedFilterMappingObject = JSON.parse(JSON.stringify(filtersResponse.backend_variable_mapping?.[backendVariable]));

        // Sort selectedFilterMappingObject with extra elements placed at the start when displayLabel is not in  alphabeticOrderFilters list else alphabetical sort(default sorted from backend)
        if (!alphabeticOrderFilters.includes(displayLabel)) {
            selectedFilterMappingObject.sort((a, b) => {
                const indexA = customOrderSub.indexOf(a);
                const indexB = customOrderSub.indexOf(b);

                // If an element is not found in customOrderSub, move it to the start (by returning -1)
                if (indexA === -1 && indexB === -1) return 0; // both are extra, maintain their order
                if (indexA === -1) return -1; // a is extra, move it to the start
                if (indexB === -1) return 1; // b is extra, move it to the start

                // Otherwise, sort based on the order in array1
                return indexA - indexB;
            });
        }
        return selectedFilterMappingObject;
    };

    const returnCheckBoxes = (cf, label, filterLabel, backendVariable, title) => {
        let result = cf[backendVariable]?.filter((o) => o.value === label);
        return (
            <FormControlLabel
                key={label}
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 12,
                    },
                }}
                style={{
                    margin: "0px 0px 0px 0px",
                    padding: "0px 0px",
                    textAlign: "left",
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 18,
                            },
                        }}
                        color="primary"
                        id={`${filterLabel}-${label}`}
                        checked={result && result.length > 0 ? result[0].checked : false}
                        onChange={() => handleOnChange(cf, label, backendVariable, title)}
                        value={label}
                    />
                }
                label={label.replace(/ \|/g, ",").trim()}
            />
        );
    };

    const handleAdd = async () => {
        const coreDataId = selectedStudy[0]?.id || "";
        let payload = {
            core_data_model: coreDataId,
            product_category: productCategoryId,
            title: userProfileName,
            user_selected_filters: getCheckedFiltersData(consumerFilters),
        };

        if (type === "create") {
            setIsLoading(true);
            try {
                const url = process.env.REACT_APP_SERVER_PATH + `/user-profile-infos/`;
                await api
                    .post(url, payload)
                    .then(() => {
                        getUserProfiles();
                        setIsLoading(false);
                        handleClose();
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        } else if (type === "update") {
            setIsLoading(true);
            const requests = selectedUserProfiles.map((profile) => {
                const url = process.env.REACT_APP_SERVER_PATH + `/user-profile-infos/${profile.id}/`;
                const payload = {
                    ...profile,
                    user_selected_filters: getCheckedFiltersData(userProfileFiltersForUpdate[profile.title]),
                };
                return api.put(url, payload);
            });

            try {
                await Promise.all(requests)
                    .then(() => {
                        getUserProfiles();
                        setIsLoading(false);
                        handleClose();
                        dispatch(showNotification({ severity: "success", data: "user profiles updated successfully." }));
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    };

    const isAddBtnDisabled = () => {
        let flag = false;
        const selectedData = getCheckedFiltersData(consumerFilters);
        if (matchedUserProfileTitle.length > 0 || userProfileName.length === 0 || profileNameError || Object.keys(selectedData).length <= 0) {
            flag = true;
        }

        return flag;
    };

    const isUpdateBtnDisabled = () => {
        let flag = false;
        const isMatched = selectedUserProfiles.some((profile) => {
            const profileFilters = profile.user_selected_filters;
            const selectedFilters = userProfileFiltersForUpdate[profile.title];
            const matched = isMatch(profileFilters, selectedFilters);
            return matched;
        });
        if (matchedUserProfileTitle.length > 0 || isMatched) {
            flag = true;
        }

        return flag;
    };

    const handleuserProfileClick = (id) => {
        setexpandedUserProfiles((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const returnFilters = (profile) => {
        if (type === "update") {
            const cf = userProfileFiltersForUpdate[profile.title];
            return (
                <div>
                    {Object.keys(cf).map((filterKey) => {
                        const filterLabel = filtersResponse.backend_variable_display_label.find((item) => item.backendVariable === filterKey)?.displayLabel;
                        const sortedData = sortSubFilters(filterLabel, filterKey);
                        return (
                            <Accordion
                                key={filterKey}
                                expanded={updateExpaned[profile.title] === filterKey}
                                onChange={handleUpdateChange(profile.title, filterKey)}
                            >
                                <AccordionSummary
                                    expandIcon={updateExpaned[profile.title] === filterKey ? <DoNotDisturbOnOutlinedIcon sx={{ color: "#005EEF" }} /> : <ControlPointRoundedIcon sx={{ color: "#005EEF" }} />}
                                    aria-controls={`${filterKey}-content`}
                                    id={`${filterKey}-content`}
                                >
                                    <Typography component="span">{filterLabel}</Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <FormGroup>
                                        {sortedData.map((label) => {
                                            return returnCheckBoxes(cf, label, filterLabel, filterKey, profile.title);
                                        })}
                                    </FormGroup>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </div>
            );
        } else {
            return (
                <div>
                    {Object.keys(consumerFilters).map((filterKey) => {
                        const filterLabel = filtersResponse.backend_variable_display_label.find((item) => item.backendVariable === filterKey)?.displayLabel;
                        const sortedData = sortSubFilters(filterLabel, filterKey);
                        return (
                            <Accordion
                                key={filterKey}
                                expanded={expanded === filterKey}
                                onChange={handleChange(filterKey)}
                            >
                                <AccordionSummary
                                    expandIcon={expanded === filterKey ? <DoNotDisturbOnOutlinedIcon sx={{ color: "#005EEF" }} /> : <ControlPointRoundedIcon sx={{ color: "#005EEF" }} />}
                                    aria-controls={`${filterKey}-content`}
                                    id={`${filterKey}-content`}
                                >
                                    <Typography component="span">{filterLabel}</Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <FormGroup>
                                        {sortedData.map((label) => {
                                            return returnCheckBoxes(consumerFilters, label, filterLabel, filterKey);
                                        })}
                                    </FormGroup>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </div>
            );
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            {/* Dialog */}
            {isOpen && (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 9,
                            backgroundColor: "rgba(0, 0, 0, 0.18)",
                        }}
                    />
                    <Box
                        role="dialog"
                        aria-modal="true"
                        sx={{
                            position: "absolute",
                            top: "3%",
                            bottom: "4%",
                            left: "16%",
                            right: "16%",
                            zIndex: 10,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "6px",
                            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.18)",
                            overflow: "auto",
                        }}
                    >
                        {/* Dialog Header */}
                        <Box
                            sx={{
                                backgroundColor: "rgb(51, 94, 234,0.1)",
                                color: "rgb(51, 94, 234)",
                                fontSize: "1.25rem",
                                minHeight: "56px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 12px",
                                position: "relative",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: "#005EEF",
                                    fontWeight: "bold",
                                }}
                            >
                                {type === "create" ? "Add New User Profile" : "Update User Profile"}
                            </Typography>

                            <IconButton
                                size="small"
                                onClick={handleClose}
                                aria-label="Close"
                                sx={{
                                    position: "absolute",
                                    right: "16px",
                                    color: "#005EEF",
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Dialog Content */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "20px",
                            }}
                        >
                            {type === "create" && (
                                <>
                                    <div>
                                        <p
                                            style={{
                                                fontSize: "0.875rem",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Enter a name for the User Profile
                                        </p>
                                        <input
                                            id="userProfileName"
                                            type="text"
                                            value={userProfileName}
                                            onChange={(e) => hanldeProfileNameChange(e.target.value)}
                                            disabled={type === "update"}
                                            autoComplete="off"
                                            style={{
                                                display: "block",
                                                maxWidth: "100%",
                                                borderRadius: "5px",
                                                outline: "none",
                                                caretColor: "var(--color-blue)",
                                                color: "rgb(48, 48, 48)",
                                                transitionDuration: "0.2s",
                                                width: "60%",
                                                border: "none",
                                                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                                                background: "#F8F8F8",
                                                padding: "6px 12px"
                                            }}
                                        />
                                        {profileNameError && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: "10px",
                                                    marginTop: "4px",
                                                }}
                                            >
                                                User Profile with given name already exists. Please choose a different name.
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ marginTop: "16px" }}>
                                        <h1 style={{ color: "#005EEF", textAlign: "center", fontSize: "1rem", fontWeight: "bold" }}>Select Filters</h1>
                                    </div>
                                    {returnFilters()}
                                </>
                            )}
                            {type === "update" &&
                                selectedUserProfiles.map((profile) => {
                                    return (
                                        <>
                                            <div
                                                key={profile.id}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    cursor: "pointer",
                                                    backgroundColor: "#F7F7F7",
                                                }}
                                                onClick={() => handleuserProfileClick(profile.id)}
                                            >
                                                <span>{profile.title}</span>
                                                {expandedUserProfiles.includes(profile.id) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                                            </div>

                                            <div
                                                style={{
                                                    padding: "10px",
                                                }}
                                            >
                                                {expandedUserProfiles.includes(profile.id) && returnFilters(profile)}
                                            </div>
                                        </>
                                    );
                                })}
                        </Box>

                        {/* Dialog Actions */}
                        <Box
                            sx={{
                                minHeight: "60px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "20px",
                            }}
                        >
                            {type === "create" ? (
                                <>
                                    <Tooltip
                                        title={matchedUserProfileTitle.length > 0 ? `The selected filter configuration already exists for '${matchedUserProfileTitle}'` : ""}
                                        placement="top-end"
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: "#DCE5F3",
                                                    color: "#000",
                                                    maxWidth: 340,
                                                    boxShadow: 2,
                                                    zIndex: 9999,
                                                },
                                            },
                                            arrow: {
                                                sx: {
                                                    color: "#F5F5F5",
                                                },
                                            },
                                            popper: {
                                                modifiers: [
                                                    {
                                                        name: "offset",
                                                        options: {
                                                            offset: [100, -18],
                                                        },
                                                    },
                                                ],
                                            },
                                        }}
                                    >
                                        <span>
                                            <Button
                                                size="small"
                                                className={isAddBtnDisabled() ? "custom-btn custom-btn--upload-reset px-4" : "custom-btn liner-light-blue-background custom-btn--upload-reset px-4"}
                                                variant="contained"
                                                onClick={handleAdd}
                                                disabled={isAddBtnDisabled()}
                                            >
                                                Add
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Button
                                        size="small"
                                        className="custom-btn custom-btn--upload-reset"
                                        variant="outlined"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Tooltip
                                        title={matchedUserProfileTitle.length > 0 ? `The selected filter configuration already exists for '${matchedUserProfileTitle}'` : ""}
                                        placement="top-end"
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: "#DCE5F3",
                                                    color: "#000",
                                                    maxWidth: 340,
                                                    boxShadow: 2,
                                                    zIndex: 9999,
                                                },
                                            },
                                            arrow: {
                                                sx: {
                                                    color: "#F5F5F5",
                                                },
                                            },
                                            popper: {
                                                modifiers: [
                                                    {
                                                        name: "offset",
                                                        options: {
                                                            offset: [140, -18],
                                                        },
                                                    },
                                                ],
                                            },
                                        }}
                                    >
                                        <span>
                                            <Button
                                                size="small"
                                                className={isUpdateBtnDisabled() ? "custom-btn custom-btn--upload-reset px-4" : "custom-btn liner-light-blue-background custom-btn--upload-reset px-4"}
                                                variant="contained"
                                                onClick={handleAdd}
                                                disabled={isUpdateBtnDisabled()}
                                            >
                                                Update
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Button
                                        size="small"
                                        className="custom-btn custom-btn--upload-reset"
                                        variant="outlined"
                                        onClick={handleUpdateReset}
                                    >
                                        Reset
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </>
    );
}

// Typechecking props for the UserProfileCreateUpdate
UserProfileCreateUpdate.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    type: PropTypes.string,
    filtersResponse: PropTypes.object,
    consumerFilters: PropTypes.object,
    setConsumerFilters: PropTypes.func,
    userProfiles: PropTypes.array,
    getUserProfiles: PropTypes.func,
    selectedUserProfiles: PropTypes.array,
};

export default UserProfileCreateUpdate;
